// System directives

"use client";

// System dependencies

import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Application dependencies

import FormInputText from "@/components/form/formInputText/FormInputText";
import FormSpinnerPage, { FormSpinnerPageProps } from "@/components/form/formSpinner/FormSpinnerPage";
import FormThemeSwitcher from "@/components/form/formThemeSwitcher/FormThemeSwitcher";
import NavLink from "@/components/nav/auth/navLink/NavLink";
import { AuthFields, AuthWaitingStatus, AuthWaitingType } from "@/_auth/interfaces/auth.interfaces";
import { Button } from "@/components/ui/button";
import { ForgotSchema } from "@/schemas/authorisationSchema";
import { Form } from "@/components/ui/form";
import { NAV_HEADER_NAME } from "@/interfaces/nav.interfaces";
import { RouteApis, RouteFields, RouteNames } from "@/interfaces/route.interfaces";
import { clearFormSpinner, formatErrorMessage, manageFormSpinner, manageFormSpinnerPage } from "@/lib/functions";
import { getAuthUserByEmailAction } from "@/_auth/actions/auth.actions";
import { supabaseClient } from "@/_auth/utils/supabase/client";

// Namespace

// const NAMESPACE = "forgot";

// devConsole.info(NAMESPACE,``);

const ForgotPage = () => {
  // Application state

  const [spinnerPageState, setSpinnerPageState] = useState<FormSpinnerPageProps | null>(null);
  const router = useRouter();

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Use effect to handle the form spinner control on component mount
  // ---------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    clearFormSpinner();
  }, []);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // React Hook Forms management
  // ---------------------------------------------------------------------------------------------------------------------------------------

  // Set the Form instance and submit handler

  const forgotForm = useForm<z.infer<typeof ForgotSchema>>({
    defaultValues: {
      [AuthFields.Email]: "",
    },
    mode: "onChange",
    resolver: zodResolver(ForgotSchema),
  });

  // Submit record

  const onForgotSubmit = forgotForm.handleSubmit(async (values: z.infer<typeof ForgotSchema>) => {
    // Run up the form spinner page.

    manageFormSpinnerPage("attempting to send reset link", setSpinnerPageState);

    // Try and send link.

    try {
      // Try and get a user for the email presented.

      const { response } = await getAuthUserByEmailAction(values[AuthFields.Email].toLowerCase());

      // See whether we have an error.

      if (response[AuthFields.Status] !== 200) {
        // To be here there is an error, put the message into a toast, clear the form spinner page and return.

        toast.error(`error experienced - ${response[AuthFields.Message]}`);
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // Make sure that we've found a matching record.

      if (!response[AuthFields.Id]) {
        // To be here there is no matching record. Put the message into a toast, clear the form spinner page and return.

        toast.error("this email does not exist, please sign up instead");
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // Set the redirectTo value.

      const redirectTo = `${process.env.NEXT_PUBLIC_APP_URL}/api/auth/callbacks${RouteApis.recovery}`;

      // Try to reset the password for email.

      const { error } = await supabaseClient.auth.resetPasswordForEmail(values[AuthFields.Email].toLowerCase(), {
        redirectTo,
      });

      // Check to see whether there was an error resetting the password for email.

      if (error) {
        // We have an error so we can put the toast up, clear the form spinner page and return.

        toast.error(formatErrorMessage(error));
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // Display a toast to let the user know the link has been sent.

      toast.success(`a reset password link has been sent to ${values[AuthFields.Email]}`);
      toast.warning(`if there are any other tabs open for ${NAV_HEADER_NAME.Prefix + NAV_HEADER_NAME.Suffix} you should close them`);

      // Reset the form spinner page.

      manageFormSpinnerPage("loading waiting page", setSpinnerPageState);

      // And push to the waiting page.

      router.push(
        `${RouteNames[RouteFields.Waiting].route}?${AuthFields.Type}=${AuthWaitingType.Recovery}&${AuthFields.Status}=${AuthWaitingStatus.Waiting}`,
      );
    } catch (error) {
      // There has been an unexpcted error. Display a toast with the error and turn off the form spinner page.

      toast.error(formatErrorMessage(error));
      manageFormSpinnerPage(null, setSpinnerPageState);
    }
  });

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Form interaction and rendering
  // ---------------------------------------------------------------------------------------------------------------------------------------

  const forgotReset = () => {
    forgotForm.reset();
  };

  return (
    <div className="relative flex h-full min-h-screen w-full flex-col justify-between bg-white px-4 py-6 dark:bg-slate-800 dark:text-white xsm:px-10 sm:h-full sm:w-full md:h-full md:w-3/5 lg:h-full lg:w-2/5 xl:h-full 3xl:px-7p 4xl:px-10p">
      <div>
        {spinnerPageState && <FormSpinnerPage isActive={spinnerPageState.isActive} caption={spinnerPageState.caption} />}
        {/* Logo */}
        <span onClick={() => manageFormSpinner(`loading ${RouteNames[RouteFields.Home].name} page - please wait`)}>
          <Link className="flex cursor-pointer space-x-3 whitespace-nowrap focus:outline-0" href={RouteNames[RouteFields.Home].route}>
            <Image alt="Logo" className="h-auto w-auto" height={28} src={"assets/logo-icon.svg"} width={28} />
            <span className="self-center whitespace-nowrap text-xl md:text-2xl">
              <span className="text-logoblue dark:text-logoblue-300">{NAV_HEADER_NAME.Prefix}</span>
              <span className="font-semibold">{NAV_HEADER_NAME.Suffix}</span>
            </span>
          </Link>
        </span>
        {/* Content */}
        <div className="flex w-full flex-col">
          <div className="mt-[45px] w-full">
            {/* Heading */}
            <div className="mb-4 flex w-full flex-col">
              <h3 className="mb-2 text-2xl font-semibold">Forgot your Password</h3>
              <p className="mb-2 text-base">{`We're sorry to hear that. Enter your email and we'll send you a reset link.`}</p>
            </div>
            {/* Form */}
            <Form {...forgotForm}>
              <form id="Forgot" noValidate onSubmit={onForgotSubmit}>
                <div className="flex w-full flex-col">
                  <FormInputText
                    control={forgotForm.control}
                    inputProps={{ style: { textTransform: "lowercase" } }}
                    label="email"
                    name={AuthFields.Email}
                    placeholder="enter email address"
                  />
                </div>
                <div className="flex w-full items-center justify-between pt-2">
                  <NavLink displaySpinner={false} label="Reset" onClick={forgotReset} />
                </div>
                <div className="my-5 flex w-full flex-col">
                  <Button className="my-2 w-full" type="submit">
                    Send Link
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="flex w-full items-center justify-between">
        <span className="flex text-sm font-normal text-gray-700 dark:text-gray-400">
          {"Already have an account?"}
          <span className="ml-1">
            <NavLink
              displaySpinner={true}
              href={RouteNames[RouteFields.SignIn].route}
              label="Sign in here"
              pageName={RouteNames[RouteFields.SignIn].name}
            />
          </span>
        </span>
        <FormThemeSwitcher />
      </div>
    </div>
  );
};

export default ForgotPage;
